<script setup lang="ts">
import SmallDropdown from "~/components/Common/Dropdowns/SmallDropdown.vue";
import UserIcon from "~/components/Header/UserIcon.vue";
import AccountDropdown from "~/components/Header/AccountDropdown.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import SvgIcon from "../Common/SvgIcon.vue";
import NotificationBadge from "../Common/NotificationBadge.vue";
import AlertCenterPanel from "../Alerts/AlertCenterPanel.vue";
import { useCompaniesStore } from "~/store/companies/companies.store";
import EditFileUploadHomeBanner from "~/components/Common/Upload/EditFileUploadHomeBanner.vue";
import { useCreateCompaniesStore } from "~/store/companies/create-company.store";
import { useAlertsStore } from "~/store/alerts/alerts.store";
import { useAlerts } from "~/composables/alerts/useAlerts";
const createCompaniesStore = useCreateCompaniesStore();
const { banner, modeEditBanner } = storeToRefs(createCompaniesStore);
const alertsStore = useAlertsStore();
const { hasActiveAlerts } = storeToRefs(alertsStore);
const { getGlobalCallbacks } = useAlerts();
const globalCallbacks = getGlobalCallbacks();
const showAccount = ref<boolean>(false);
const userIcon = ref();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const dropdownPosition = ref({ top: 0, left: 0 });
const companiesStore = useCompaniesStore();
const { companyInfos } = storeToRefs(companiesStore);
const showEditBannerButton = ref<boolean>(false);
const editMode = ref(false);

declare global {
  interface Window {
    Beacon: any;
  }
}

function openHelpscout() {
  window.Beacon("open");
}

const eventToggleOffDropdown = (event: FocusEvent) => {
  if (event?.relatedTarget instanceof HTMLElement) {
    if (event.relatedTarget.classList.contains("disconnect_button")) {
      return;
    }
  }
  showAccount.value = false;
};

watch(showAccount, (newVal) => {
  if (newVal) {
    const rect = userIcon.value.$el.getBoundingClientRect();
    dropdownPosition.value = { top: rect.bottom, left: rect.left - 150 };
  }
});

onMounted(async () => {
  await nextTick(async () => {
    await companiesStore.fetchCompanyInfos();
  });
});

const heightHeader = computed(() => {
  if (companyInfos?.value?.welcomeMessage) {
    return companyInfos?.value?.welcomeMessage?.length < 330 ? "176px" : "96px";
  }
  return "176px";
});

const updateWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});

const updateBanner = async () => {
  if (companyInfos.value?.companyId) {
    if (banner.value && banner.value instanceof File) {
      await createCompaniesStore.convertCompanyBanner(banner.value as File);
    }
    if (banner.value instanceof File || !banner.value) {
      await createCompaniesStore.updateCompanyBanner(companyInfos.value?.companyId);
      await companiesStore.fetchCompanyInfos();
    }
  }
};

const windowWidth = ref(1024);

const modeEdit = (bEditMode: boolean) => {
  editMode.value = bEditMode;
  modeEditBanner.value = bEditMode;
};
</script>

<template>
  <div class="relative">
    <nav
      id="header_container"
      class="w-full"
      :style="{
        '--banner-url': companyInfos?.bannerUrl ? `url(${companyInfos.bannerUrl})` : 'none',
        height:
          windowWidth >= 1024
            ? companyInfos?.bannerUrl || modeEditBanner
              ? heightHeader
              : companyInfos?.welcomeMessage && companyInfos?.welcomeMessage?.length > 330
                ? '63px'
                : '117px'
            : '100%',
      }"
      :class="windowWidth >= 1024 ? 'header' : 'header-mobile border-b-[1px] border-stroke-color px-6 py-4 '"
      @mouseover="showEditBannerButton = true"
      @mouseleave="showEditBannerButton = false"
    >
      <div class="flex flex-row items-center justify-between">
        <div class="w-full lg:hidden">
          <span class="heading_3_med">Bienvenue 👋</span>
        </div>
        <div
          class="flex flex-row w-full items-center gap-[8px] lg:gap-[16px] justify-end"
          :class="{ 'absolute right-[32px] top-[8px] z-10': windowWidth >= 1024 }"
        >
          <NotificationBadge
            v-if="authStore.isLoggedIn()"
            :show="hasActiveAlerts"
            :count="alertsStore.activeAlertsCount"
            color="var(--red-main)"
          >
            <SvgIcon
              class="button_bell"
              name="bell"
              :style="{ width: '18px', height: '18px' }"
              @click="alertsStore.togglePanel()"
            />
          </NotificationBadge>
          <SvgIcon class="button_tootip" name="tooltip" :style="{ height: '16px' }" @click="openHelpscout"></SvgIcon>
          <SmallDropdown></SmallDropdown>
          <span v-if="authStore.isLoggedIn()" class="w-px h-5 bg-stroke-color shrink-0" />
          <UserIcon
            v-if="authStore.isLoggedIn()"
            ref="userIcon"
            :initials="
              user?.firstname && user?.lastname ? user?.firstname?.charAt(0) + user?.lastname?.charAt(0) : undefined
            "
            @click="showAccount = !showAccount"
            @focusout="eventToggleOffDropdown"
          ></UserIcon>
        </div>
        <EditFileUploadHomeBanner
          v-if="authStore.isSimbelAdmin && windowWidth >= 1024"
          v-model="banner"
          :show-edit-banner-button="showEditBannerButton"
          @updateBanner="updateBanner"
          @edit-mode="modeEdit"
        ></EditFileUploadHomeBanner>
      </div>
    </nav>
    <Teleport to="body">
      <AccountDropdown
        v-if="authStore.isLoggedIn()"
        :showAccount="showAccount"
        :style="{ top: dropdownPosition.top + 'px', left: dropdownPosition.left + 'px' }"
        @close="showAccount = false"
      ></AccountDropdown>
    </Teleport>
    <AlertCenterPanel v-if="alertsStore.isGlobalPanel" :callbacks="globalCallbacks" />
  </div>
</template>

<style scoped lang="scss">
.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--banner-url);
  /* background-image: url("./images.jpeg"); */
  background-position: top;
  background-repeat: no-repeat;
  background-size: cover;
  opacity: 0.6;
  z-index: -1;
}
.header {
  @apply flex flex-col  gap-[18px] relative;
  z-index: 0;
}

.header-mobile {
  @apply flex flex-col gap-[18px] relative;
  z-index: 0;
}

.button_bell {
  @apply cursor-pointer text-secondary-color;

  &:hover {
    @apply text-primary-color;
  }
}

.button_tootip {
  @apply cursor-pointer text-secondary-color;

  &:hover {
    color: var(--blue-deep-dark);
  }
}
</style>
